
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { 
  Form, 
  FormControl, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Layout } from "@/components/Layout";
import { motion } from "framer-motion";
import { useAuth } from "@/lib/hooks/auth";
import { Mail, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";

const formSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
  rememberMe: z.boolean().default(false),
});

type FormValues = z.infer<typeof formSchema>;

const Login = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { signIn, user, loading } = useAuth();
  const { t } = useTranslation();
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  // Redirect if already logged in - but only after auth has finished loading
  useEffect(() => {
    if (loading) {
      console.log("[Login] Auth still loading, waiting...");
      return; // Don't do anything while loading
    }
    
    console.log("[Login] Auth loaded, user state:", user);
    
    if (user) {
      console.log("[Login] User already logged in, redirecting");
      const redirectPath = user.isAdmin ? "/admin" : "/dashboard";
      console.log("[Login] Redirecting to:", redirectPath);
      navigate(redirectPath, { replace: true });
    }
  }, [user, navigate, loading]);
  
  const onSubmit = async (values: FormValues) => {
    if (isSubmitting) {
      console.log("[Login] Already submitting, ignoring duplicate request");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log(`[Login] Attempting login with ${values.email}`);
      const result = await signIn(values.email, values.password);
      
      if (!result.success) {
        console.error("[Login] Login failed:", result.error);
        toast.error("Login Failed", {
          description: result.error || "Invalid credentials. Please try again.",
        });
        setIsSubmitting(false);
        return;
      }
      
      // Successful login
      console.log("[Login] Login successful");
      toast.success("Login Successful", {
        description: "You have successfully logged in to your account.",
      });
      
      // The AuthProvider will handle the redirection based on user role
    } catch (error) {
      console.error('[Login] Error during login:', error);
      toast.error("Login Failed", {
        description: "There was a problem logging in. Please check your credentials and try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // If auth is still loading, show a loading indicator
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">{t('common.loading')}</p>
          </div>
        </div>
      </Layout>
    );
  }
  
  // If user is already authenticated, don't render the login form
  if (user) {
    return null; // Don't render anything while redirect happens
  }
  
  return (
    <Layout>
      <section className="py-12 md:py-20 bg-gradient-to-b from-clay-50 to-background">
        <div className="container max-w-md mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-card border border-border rounded-xl p-8 shadow-sm"
          >
            <div className="text-center mb-8">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-clay-100 text-clay-800 rounded-full mb-4">
                {t('auth.welcomeBack')}
              </span>
              <h1 className="text-2xl md:text-3xl font-display font-bold mb-2">{t('auth.loginTitle')}</h1>
              <p className="text-muted-foreground">
                {t('auth.loginSubtitle')}
              </p>
            </div>
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('auth.email')}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                          <Input type="email" placeholder={t('auth.emailPlaceholder')} className="pl-9" {...field} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('auth.password')}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                          <Input type="password" placeholder={t('auth.passwordPlaceholder')} className="pl-9" {...field} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <div className="flex items-center justify-between">
                  <FormField
                    control={form.control}
                    name="rememberMe"
                    render={({ field }) => (
                      <FormItem className="flex items-center space-x-2 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel className="text-sm cursor-pointer">{t('auth.rememberMe')}</FormLabel>
                      </FormItem>
                    )}
                  />
                  
                  <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                    {t('auth.forgotPassword')}
                  </Link>
                </div>
                
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? t('auth.loggingIn') : t('auth.loginButton')}
                </Button>
              </form>
            </Form>
            
            <div className="mt-6">
              <div className="flex items-center gap-2 my-4">
                <Separator className="flex-grow" />
                <span className="text-xs text-muted-foreground">{t('auth.or')}</span>
                <Separator className="flex-grow" />
              </div>
              
              <p className="text-center text-sm text-muted-foreground">
                {t('auth.noAccount')}{" "}
                <Link to="/register" className="text-primary hover:underline">
                  {t('auth.signUp')}
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default Login;
